import type { SupabaseClient } from '@supabase/supabase-js';

export interface PurchaseOrderFileEdit {
  id: string;
  mpo_number: string | null;
}

/**
 * Saves each file's manufacturer and mPO number, then keeps
 * purchase_orders.po_number aligned when a single file remains.
 * Throws on the first failed update so the caller never reports success
 * for a partial save. An update that matches no row (deleted meanwhile, or
 * hidden by RLS) returns no error, so each one must also return its row.
 */
export async function savePurchaseOrderFileEdits(
  client: Pick<SupabaseClient, 'from'>,
  purchaseOrderId: string,
  manufacturerName: string,
  files: PurchaseOrderFileEdit[],
): Promise<void> {
  for (const file of files) {
    const { data, error } = await client
      .from('purchase_order_files')
      .update({
        manufacturer_name: manufacturerName,
        mpo_number: file.mpo_number?.trim() || null,
      })
      .eq('id', file.id)
      .select('id');
    if (error) throw error;
    if (!data?.length) throw new Error(`Purchase order file ${file.id} was not updated`);
  }

  const singleMpoNumber = files.length === 1 ? files[0].mpo_number?.trim() : undefined;
  if (singleMpoNumber) {
    const { data, error } = await client
      .from('purchase_orders')
      .update({ po_number: singleMpoNumber })
      .eq('id', purchaseOrderId)
      .select('id');
    if (error) throw error;
    if (!data?.length) throw new Error(`Purchase order ${purchaseOrderId} was not updated`);
  }
}
